<script setup>
import { computed, reactive, ref, watch } from "vue";
import { languageName, trans } from "i18n";
import { formatAddress, getErrorMessage, sortByKey } from "lib/modules/util";
import Http from "lib/modules/Http";
import Accordion from "Accordion";

const props = defineProps(["srvId", "ccyId", "tracking", "now", "booking", "pack", "params", "host", "dirty"]);

const emit = defineEmits(["cancel", "confirm", "update:busy", "stateChange"]);

const numberFormat = new Intl.NumberFormat(document.documentElement.lang);
const dateFormat = new Intl.DateTimeFormat(document.documentElement.lang, { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "numeric" });
const timeZone = new Intl.DateTimeFormat().resolvedOptions().timeZone;

let addExistingTimeslot = true;

const guestParts = computed(() => {
	return props.booking ? props.booking.parts.filter((p) => p.role === "guest") : [];
});

const state = reactive({
	...props.params,
	promo: false,
	guests: guestParts.value.map((p) => p.user_id)
});

const booking = reactive(new Http({})); // eslint-disable-line vue/no-dupe-keys
const srv = reactive(new Http({
	org: {
		branches: []
	},
	users: []
}));

const isDirty = ref(Boolean(props.dirty));
const refresh = ref(false);

const enabledUsers = computed(() => {
	const usrs = srv.data.users.filter((user) => user.enabled && user.langs.length > 0 && !guestParts.value.some((guest) => guest.user_id === user.user_id));

	return sortByKey(usrs, "name");
});

const methods = computed(() => {
	const mets = [];

	if (enabledUsers.value.length === 0)
		return [];

	const available = enabledUsers.value.some((user) => user.timeslots.length > 0);

	if (srv.data.video)
		mets.push({ method: "video", name: trans.gettext("Video"), icon: "fa-video", hint: trans.gettext("Vixly video service (in-browser)"), available });

	if (srv.data.office)
		mets.push({ method: "office", name: trans.gettext("In-person"), icon: "fa-building", hint: trans.gettext("Meet in-person at location"), available });

	if (srv.data.phone)
		mets.push({ method: "phone", name: trans.gettext("Phone"), icon: "fa-phone", hint: trans.gettext("We will call your provided number"), available });

	return mets;
});

const method = computed(() => getSelected(methods.value.filter((met) => met.available), "method"));

const branches = computed(() => {
	if (!method.value || method.value.method !== "office")
		return [];

	const brchs = [];
	let usrs = getUsersByBranch(null);

	if (usrs.length) {
		brchs.push({
			branch_id: null,
			addr: srv.data.org.addr,
			addr2: srv.data.org.addr2,
			zip: srv.data.org.zip,
			city: srv.data.org.city,
			county: srv.data.org.county,
			country_id: srv.data.org.country_id,
			name: formatAddress(srv.data.org),
			available: usrs.some((user) => user.timeslots.length > 0)
		});
	}

	for (const branch of srv.data.org.branches) {
		usrs = getUsersByBranch(branch.branch_id);

		if (!usrs.length)
			continue;

		brchs.push({
			branch_id: branch.branch_id,
			addr: branch.addr,
			addr2: branch.addr2,
			zip: branch.zip,
			city: branch.city,
			county: branch.county,
			country_id: branch.country_id,
			name: formatAddress(branch),
			available: usrs.some((user) => user.timeslots.length > 0)
		});
	}
	return sortByKey(brchs, "name");
});

const branch = computed(() => getSelected(branches.value.filter((bra) => bra.available), "branch_id"));

const languages = computed(() => {
	if (!method.value || (method.value.method === "office" && !branch.value))
		return [];

	const langs = {};
	let usrs = enabledUsers.value;

	if (method.value.method === "office")
		usrs = getUsersByBranch(branch.value.branch_id);

	for (const user of usrs) {
		for (const lang of user.langs) {
			if (!(lang in langs))
				langs[lang] = { lang_id: lang, name: languageName(lang), available: false };

			if (user.timeslots.length > 0)
				langs[lang].available = true;
		}
	}

	return sortByKey(Object.values(langs), "name");
});

const language = computed(() => getSelected(languages.value.filter((lang) => lang.available), "lang_id"));

const users = computed(() => {
	if (!method.value || (method.value.method === "office" && !branch.value) || !language.value)
		return [];

	if (method.value.method === "office")
		return enabledUsers.value.filter((user) => user.branch_id === branch.value.branch_id && user.langs.includes(language.value.lang_id));

	return enabledUsers.value.filter((user) => user.langs.includes(language.value.lang_id));
});

const user = computed(() => getSelected(users.value.filter((usr) => usr.timeslots.length > 0), "user_id"));

const timeslots = computed(() => {
	const slots = {};
	let usrs;

	if (srv.data.puser_select)
		usrs = user.value ? [user.value] : [];
	else
		usrs = users.value;

	for (const usr of usrs) {
		for (const start of usr.timeslots) {
			if (start in slots)
				slots[start].users.push(usr.user_id);
			else
				slots[start] = { start, users: [usr.user_id] };
		}
	}

	return Object.values(slots);
});

const timeslot = computed(() => getSelected(timeslots.value, "start", false));

const bookable = computed(() => methods.value.length > 0 && (props.booking || (srv.data.org.eos > props.now + 300 && srv.data.enabled)));

function track(action) {
	$vixAnalytics({
		ca: 1,
		e_c: "Booking",
		e_a: "Booking " + action
	});
}

function getSelected(array, key, auto=true) {
	if (key in state)
		return array.find((item) => item[key] === state[key]) || null;
	else if (auto && array.length === 1)
		return array[0];

	return null;
}

function getUsersByBranch(id) {
	return enabledUsers.value.filter((usr) => usr.branch_id === id);
}

function getSrvBookingData() {
	if (props.booking) {
		srv.get(`/api/srv/${props.srvId}/bookingdata?duration=${props.booking.duration + props.booking.pause}&host=${props.host ? 1 : 0}`, () => {
			const usr = srv.data.users.find((u) => u.user_id === props.booking.puser_id);

			if (addExistingTimeslot && usr)
				usr.timeslots.push(props.booking.start);

			if (props.host)
				srv.data.puser_select = 1;
		});
	}
	else
		srv.get(`/api/srv/${props.srvId}/bookingdata`);
}

function _book(data, usrs) {
	const met = props.booking ? "patch" : "post";
	const url = props.booking ? `/api/booking/${props.booking.booking_id}` : "/api/booking";
	const prefUsrIdx = props.booking ? usrs.indexOf(props.booking.puser_id) : -1;
	const usrIdx = prefUsrIdx >= 0 ? prefUsrIdx : Math.floor(Math.random() * usrs.length);

	data.puser_id = usrs[usrIdx];

	booking[met](url, data, () => {
		if (props.booking)
			emit("confirm");
		else {
			booking.busy = true;

			if (props.tracking && booking.data.pay_url)
				track("Payment Start");

			location.href = booking.data.pay_url || `booked/${booking.data.booking_id}`;
		}
	}, () => {
		console.log("booking failed:", booking.error);

		if (props.booking && props.booking.start === data.start && props.booking.puser_id === data.puser_id && booking.error === "provider busy")
			addExistingTimeslot = false;

		if (usrs.length > 1 && booking.error.match(/^(provider busy|user not found|user\/\w+ org mismatch|provider don't speak|duplicate entry: )/)) {
			usrs.splice(usrIdx, 1);
			_book(data, usrs);
		}
		else if (booking.error.match(/^(srv not enabled|provider busy|too late for booking|too early for booking|user not found|booking not allowed|user\/\w+ org mismatch|method \w+ not supported|provider don't speak)/)) {
			booking.error = null;
			refresh.value = true;
			getSrvBookingData();
		}
		else if (booking.error.match(new RegExp(`^duplicate entry: .+${data.puser_id}`)))
			booking.error = trans.gettext("Host and client must be different user accounts!");
	});
}

function book() {
	const data = {
		srv_id: props.srvId,
		start: timeslot.value.start,
		method: method.value.method,
		ccy_id: props.ccyId,
		lang_id: language.value.lang_id,
		notes: state.notes,
		promo: state.promo,
		pack: props.pack,
		...props.booking && { guests: state.guests }
	};

	_book(data, [...timeslot.value.users]);

	if (props.tracking)
		track("Confirm");
}

watch(() => booking.busy, (val) => emit("update:busy", val));

watch(state, () => {
	isDirty.value = true;
	emit("stateChange", state);
});

if (props.tracking) {
	watch(() => state.method,    () => track("Select Method"));
	watch(() => state.branch_id, () => track("Select Branch"));
	watch(() => state.lang_id,   () => track("Select Language"));
	watch(() => state.user_id,   () => track("Select Host"));
	watch(() => state.start,     () => track("Select Time"));

	track("Start");
}

getSrvBookingData();
srv.busy = "init";
</script>

<template>
	<div class="vix-booking">
		<form @submit.prevent="timeslot && book()">
			<fieldset :disabled="srv.busy || booking.busy">
				<div v-if="srv.busy === 'init'" class="loading"><i class="fa-regular fa-xl fa-spin fa-spinner-third"></i></div>
				<div v-else-if="srv.error" class="vui-error not-bookable card-24">{{ getErrorMessage(srv.error) }}</div>
				<div v-else-if="!bookable" class="not-bookable card-24">{{ $gettext("This service is not bookable.") }}</div>
				<div v-else class="grid gap-8">
					<div v-if="methods.length > 0 && !methods[0].available" class="not-bookable card-24">{{ $gettext("There are currently no available appointments for this service.") }}</div>
					<div class="grid gap-16 align-self-start">
						<Accordion icon="fa-globe" :title="props.booking ? $gettext('Appointment') : $gettext('Book appointment')" :value="method && method.name">
							<div class="grid gap-12">
								<UiRadioButton
									v-for="met in methods"
									:key="met.method"
									:model-value="method && method.method"
									name="method"
									class="vui-radio-custom"
									:disabled="!met.available"
									:value="met.method"
									@update:model-value="state.method = $event"
								>
									<i :class="met.icon" class="fa-regular fa-fw" style="margin-right:6px"></i>
									<span>{{ met.name }}</span>
									<div class="vui-hint">{{ met.hint }}</div>
								</UiRadioButton>
							</div>
						</Accordion>
						<Accordion v-if="method && method.method === 'office'" icon="fa-location-dot" :title="$gettext('Location')" :value="branch && branch.name">
							<div class="grid gap-12">
								<UiRadioButton
									v-for="bra in branches"
									:key="bra.branch_id"
									:model-value="branch ? branch.branch_id : false"
									name="branch"
									class="vui-radio-custom"
									:disabled="!bra.available"
									:label="bra.name"
									:value="bra.branch_id"
									@update:model-value="state.branch_id = $event"
								/>
							</div>
						</Accordion>
						<div v-if="!host && branch" class="card-8">
							<UiMap :address="branch"/>
						</div>
						<Accordion v-if="method && (method.method !== 'office' || branch)" icon="fa-language" :title="$gettext('Language')" :value="language && language.name">
							<div class="grid gap-12">
								<UiRadioButton
									v-for="lang in languages"
									:key="lang.lang_id"
									:model-value="language && language.lang_id"
									name="language"
									class="vui-radio-custom"
									:disabled="!lang.available"
									:label="lang.name"
									:value="lang.lang_id"
									@update:model-value="state.lang_id = $event"
								/>
								<div class="vui-hint">{{ $gettext("Preferred spoken language") }}</div>
							</div>
						</Accordion>
						<Accordion v-if="language && srv.data.puser_select" icon="fa-user" :title="$gettext('Host')" :value="user && user.name" class="user">
							<div class="grid gap-12">
								<UiRadioButton
									v-for="usr in users"
									:key="usr.user_id"
									:model-value="user && user.user_id"
									name="user"
									class="vui-radio-custom"
									:disabled="usr.timeslots.length === 0"
									:value="usr.user_id"
									@update:model-value="state.user_id = $event"
								>
									<img class="portrait" :src="`/api/user/${usr.user_id}/img/portrait`">
									<div>
										<div>{{ usr.name }}</div>
										<div class="title">{{ usr.title }}</div>
									</div>
								</UiRadioButton>
							</div>
						</Accordion>
						<Accordion v-if="language && (!srv.data.puser_select || user)" icon="fa-calendar-days" :title="$gettext('Time')" :value="timeslot && dateFormat.format(timeslot.start * 1000)">
							<UiDateTimePicker
								v-model="state.start"
								:timeslots="timeslots"
							>
								<div class="vui-hint">{{ $gettext("Time zone: %1", timeZone) }}</div>
							</UiDateTimePicker>
						</Accordion>
						<template v-if="timeslot">
							<!-- Use native textarea instead of UiTextarea to trigger dirty state on each keystroke (on input) -->
							<div v-if="!host" class="vui-textarea">
								<textarea
									v-model="state.notes"
									:placeholder="$gettext('Questions / comments')"
									maxlength="200"
									rows="2"
								></textarea>
							</div>
							<UiCheckbox v-if="!props.booking" v-model="state.promo" :label="$gettext('I accept to receive Vixly e-mails with news from %1', srv.data.org.name)"/>
							<fieldset v-else-if="guestParts.length > 0">
								<legend>{{ $gettext("Guests") }}</legend>
								<div class="grid gap-8">
									<UiCheckbox
										v-for="part in guestParts"
										:key="part.user_id"
										v-model="state.guests"
										:value="part.user_id"
									>
										<span>{{ part.fname }} {{ part.lname }}<template v-if="part.title"> ({{ part.title }})</template></span>
									</UiCheckbox>
								</div>
							</fieldset>
						</template>
					</div>
					<template v-if="timeslot && !props.booking">
						<div v-if="srv.data.org.demo" class="confirm-info">{{ $gettext("This is a demo and the service can therefore not be booked.") }}</div>
						<div v-else-if="!srv.data.org.pay_enabled && srv.data.price !== null" class="confirm-info">{{ $gettext("The service is not bookable due to missing payment setup. Providers on Vixly can complete Stripe payment setup via the admin interface.") }}</div>
						<div v-else class="confirm-info">
							<template v-if="srv.data.price !== null">
								<span v-if="pack || srv.data.pack">{{ $ngettext("This booking is part of a package. Please note that no refund will be issued upon cancellation. You may cancel the appointment up to %1 hour before the scheduled time to regain the package credit, which can be used for a future booking.", "This booking is part of a package. Please note that no refund will be issued upon cancellation. You may cancel the appointment up to %1 hours before the scheduled time to regain the package credit, which can be used for a future booking.", srv.data.org.cancel_hrs) }}</span>
								<span v-else-if="srv.data.org.ext_pay">
									{{ $ngettext("You may cancel your booking at any time %1 hour prior to the appointment at no cost. For cancellation less than %1 hour prior, the cost is 100%.", "You may cancel your booking at any time %1 hours prior to the appointment at no cost. For cancellation less than %1 hours prior, the cost is 100%.", srv.data.org.cancel_hrs) }}
								</span>
								<span v-else>
									{{ $ngettext("You may cancel your booking at any time %1 hour prior to the appointment with a %2%% refund. No refund for cancellation less than %1 hour prior.", "You may cancel your booking at any time %1 hours prior to the appointment with a %2%% refund. No refund for cancellation less than %1 hours prior.", srv.data.org.cancel_hrs, numberFormat.format(srv.data.org.refund_rate)) }}
								</span>
								<span>&nbsp;</span>
							</template>
							<span v-if="srv.data.resched_hrs === 0">{{ $gettext("This booking is not possible to reschedule.") }}</span>
							<span v-else>{{ $ngettext("You may reschedule up to %1 hour prior to the appointment.", "You may reschedule up to %1 hours prior to the appointment.", srv.data.resched_hrs) }}</span>
						</div>
						<UiButton type="submit" :busy="booking.busy" :disabled="(!srv.data.org.pay_enabled && srv.data.price !== null) || srv.data.org.demo">{{ $gettext("Confirm") }}</UiButton>
					</template>
					<div v-if="!host && props.booking && props.booking.resched_hrs > 0" class="confirm-info">{{ $ngettext("You may reschedule up to %1 hour prior to the appointment.", "You may reschedule up to %1 hours prior to the appointment.", props.booking.resched_hrs) }}</div>
					<div class="vui-error" :class="{ 'justify-self-end': props.booking }">{{ getErrorMessage(booking.error) }}</div>
					<div v-if="props.booking" class="grid gap-16 cols-2 justify-self-end">
						<button type="button" class="vui-btn outlined" @click="$emit('cancel')">{{ $gettext("Cancel") }}</button>
						<UiButton type="submit" :busy="booking.busy" :disabled="!timeslot || !isDirty">{{ $gettext("Confirm") }}</UiButton>
					</div>
				</div>
			</fieldset>
		</form>
		<UiDialog v-if="refresh" modal>
			<h2>{{ $gettext("Appointment not available") }}</h2>
			<div class="grid gap-28">
				<div>{{ $gettext("The specified appointment is no longer available, please try again.") }}</div>
				<button class="vui-btn justify-self-end" @click="refresh = false">{{ $gettext("OK") }}</button>
			</div>
		</UiDialog>
	</div>
</template>
